import { useCallback, useEffect, useState } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';

import {
  buildEmptyRecentAddressFormValues,
  buildRecentAddressFormValues,
  findMatchingRecentAddress,
  type IStoreRecentAddressFormSnapshot,
} from './storeRecentAddress.utils';
import type { IRecentAddressSuggestion } from '@/common/interfaces/mostRecent.interface';

interface IUseStoreRecentAddressSelectionParams<TFieldValues extends FieldValues> {
  recentAddresses: IRecentAddressSuggestion[];
  shippingForm: UseFormReturn<TFieldValues>;
  isReadOnly: boolean;
  onAddressApplied?: () => void;
}

export const useStoreRecentAddressSelection = <TFieldValues extends FieldValues>({
  recentAddresses,
  shippingForm,
  isReadOnly,
  onAddressApplied,
}: IUseStoreRecentAddressSelectionParams<TFieldValues>) => {
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);

  const applyFormValues = useCallback(
    (values: IStoreRecentAddressFormSnapshot) => {
      shippingForm.setValue(
        'shippingName' as never,
        (values.shippingName == null ? '' : String(values.shippingName)) as never,
        { shouldValidate: true }
      );
      shippingForm.setValue('attentionName' as never, (values.attentionName ?? '') as never, {
        shouldValidate: true,
      });
      shippingForm.setValue('street1' as never, (values.street1 ?? '') as never, {
        shouldValidate: true,
      });
      shippingForm.setValue('street2' as never, (values.street2 ?? '') as never, {
        shouldValidate: true,
      });
      shippingForm.setValue('city' as never, (values.city ?? '') as never, {
        shouldValidate: true,
      });
      shippingForm.setValue('state' as never, (values.state ?? { label: '', value: '' }) as never, {
        shouldValidate: true,
      });
      shippingForm.setValue('shippingZipCode' as never, (values.shippingZipCode ?? '') as never, {
        shouldValidate: true,
      });
      onAddressApplied?.();
    },
    [onAddressApplied, shippingForm]
  );

  const syncSelectionFromForm = useCallback(
    (formSnapshot: IStoreRecentAddressFormSnapshot) => {
      const matchingAddress = findMatchingRecentAddress(formSnapshot, recentAddresses);

      if (matchingAddress) {
        setSelectedAddressId((currentId) =>
          currentId === matchingAddress.id ? currentId : matchingAddress.id
        );
        return;
      }

      setSelectedAddressId(null);
    },
    [recentAddresses]
  );

  useEffect(() => {
    syncSelectionFromForm(shippingForm.getValues() as IStoreRecentAddressFormSnapshot);
  }, [recentAddresses, shippingForm, syncSelectionFromForm]);

  useEffect(() => {
    const subscription = shippingForm.watch((formSnapshot) => {
      syncSelectionFromForm(formSnapshot as IStoreRecentAddressFormSnapshot);
    });

    return () => subscription.unsubscribe();
  }, [shippingForm, syncSelectionFromForm]);

  const handleRecentAddressSelect = useCallback(
    (address: IRecentAddressSuggestion) => {
      if (isReadOnly) {
        return;
      }

      if (selectedAddressId === address.id) {
        setSelectedAddressId(null);
        applyFormValues(buildEmptyRecentAddressFormValues());
        return;
      }

      setSelectedAddressId(address.id);
      const currentAttentionName = String(shippingForm.getValues('attentionName' as never) ?? '');
      applyFormValues(buildRecentAddressFormValues(address, currentAttentionName));
    },
    [applyFormValues, isReadOnly, selectedAddressId, shippingForm]
  );

  return {
    selectedAddressId,
    handleRecentAddressSelect,
  };
};
